import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CheckCircle2, XCircle, Save, Zap } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AiBudgetSection } from "@/components/admin/AiBudgetSection";

export default function AutomationAiConfigTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.adminAutoAiConfigGet().then((c) => { if (!cancelled) setCfg(c); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!cfg) return <div className="p-6 text-textgray"><Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Cargando…</div>;

  const setField = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const { key_present, id, updated_at, updated_by, ...body } = cfg;
      const saved = await api.adminAutoAiConfigPut(body);
      setCfg(saved);
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.adminAutoAiConfigTest();
      setTestResult(r);
    } finally { setTesting(false); }
  };

  return (
    <section className="max-w-2xl space-y-6">
      <div>
        <h2 className="font-serif text-lg font-bold text-blueblack">Configuración de IA</h2>
        <p className="text-xs text-textgray">
          La clave se lee desde <code>EMERGENT_LLM_KEY</code> en el servidor y nunca se envía al navegador.
        </p>
      </div>

      <div className={`p-3 flex items-center gap-2 text-sm border ${cfg.key_present ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
        {cfg.key_present ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
        {cfg.key_present ? "Clave configurada en el backend." : "No hay clave configurada. Contactá al administrador del servidor."}
      </div>

      <div className="bg-white border border-softblue p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-textgray">Proveedor</Label>
            <Input value={cfg.provider} onChange={(e) => setField({ provider: e.target.value })} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-textgray">Modelo</Label>
            <Input value={cfg.model} onChange={(e) => setField({ model: e.target.value })} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-textgray">Longitud máx (tokens)</Label>
            <Input type="number" value={cfg.max_tokens} onChange={(e) => setField({ max_tokens: Number(e.target.value) })} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-textgray">Nivel de creatividad</Label>
            <Input type="number" step="0.05" min="0" max="1" value={cfg.creativity} onChange={(e) => setField({ creativity: Number(e.target.value) })} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-textgray">Tono editorial</Label>
            <Input value={cfg.tone} onChange={(e) => setField({ tone: e.target.value })} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-textgray">Máximo por lote</Label>
            <Input type="number" value={cfg.batch_size} onChange={(e) => setField({ batch_size: Number(e.target.value) })} data-testid="ai-config-batch-size" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-textgray">Longitud preferida</Label>
            <Select value={cfg.preferred_length || "medium"} onValueChange={(v) => setField({ preferred_length: v })}>
              <SelectTrigger data-testid="ai-config-preferred-length"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="short">Corta (180-250 palabras)</SelectItem>
                <SelectItem value="medium">Media (350-500 palabras)</SelectItem>
                <SelectItem value="long">Larga (650-900 palabras)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-textgray">Timeout (s)</Label>
            <Input type="number" value={cfg.timeout_seconds} onChange={(e) => setField({ timeout_seconds: Number(e.target.value) })} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-textgray">Reintentos</Label>
            <Input type="number" value={cfg.retries} onChange={(e) => setField({ retries: Number(e.target.value) })} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-textgray">Límite diario</Label>
            <Input type="number" value={cfg.daily_limit} onChange={(e) => setField({ daily_limit: Number(e.target.value) })} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-textgray">Límite diario de fallos</Label>
            <Input type="number" value={cfg.daily_failure_limit ?? 50} onChange={(e) => setField({ daily_failure_limit: Number(e.target.value) })} data-testid="ai-config-daily-failure-limit" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-textgray">Instrucción editorial</Label>
          <Textarea rows={5} value={cfg.instructions || ""} onChange={(e) => setField({ instructions: e.target.value })}
                    placeholder="Instrucción base. Si se deja vacía, se usa la instrucción periodística por defecto." />
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={save} disabled={saving} className="gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
        </Button>
        <Button variant="outline" onClick={runTest} disabled={testing || !cfg.key_present} className="gap-2">
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Probar conexión
        </Button>
        {testResult && (
          <div className={`text-sm px-3 py-1 border flex items-center gap-2 ${testResult.ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
            {testResult.ok ? <><CheckCircle2 className="w-4 h-4" /> {testResult.sample_response}</> : <><XCircle className="w-4 h-4" /> {testResult.error}</>}
          </div>
        )}
      </div>

      <AiBudgetSection />
    </section>
  );
}
